import React, { useState, useMemo, useEffect } from 'react';
import { useData } from '../DataContext';
import { useLang } from '../LanguageContext'; // ★ 언어 훅
import { useNavigate } from 'react-router-dom';
import { 
  FileText, Search, Filter, Calendar, RotateCcw, DollarSign, 
  ArrowDownLeft, CheckCircle, AlertCircle, X, ShieldCheck,
  ChevronLeft, ChevronRight // ★ 페이지네이션 아이콘 추가
} from 'lucide-react';

const CreditManagement = () => {
  const { credits = [], agents = [], creditVault, repayLoan } = useData(); 
  const { t } = useLang(); // ★ 번역 함수
  const navigate = useNavigate();

  const [filterType, setFilterType] = useState('All'); 
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // ★ [추가] 페이지네이션 상태 관리
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [isRepayModalOpen, setIsRepayModalOpen] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [repayAmount, setRepayAmount] = useState('');

  const fmt = (num) => num ? Math.floor(Number(num)).toLocaleString() : '0';
  const parseNum = (str) => parseFloat(String(str).replace(/,/g, '')) || 0;

  // --- 에이전트 클릭 핸들러 ---
  const handleAgentClick = (agentCode) => {
      navigate('/agents', { state: { targetAgentCode: agentCode } });
  };

  // ★ [추가] 필터 조건이 바뀌면 페이지를 1로 초기화
  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, searchTerm, startDate, endDate]);

  const filteredCredits = useMemo(() => {
    let data = Array.isArray(credits) ? credits : [];
    if (searchTerm) {
      const lowerTerm = searchTerm.toLowerCase();
      data = data.filter(c => 
        (c.agentId && c.agentId.toLowerCase().includes(lowerTerm)) ||
        (c.gameId && c.gameId.toLowerCase().includes(lowerTerm)) ||
        (c.loanId && c.loanId.toLowerCase().includes(lowerTerm))
      );
    }
    if (filterType !== 'All') data = data.filter(c => c.status === filterType);
    if (startDate) data = data.filter(c => c.createdAt >= startDate);
    if (endDate) data = data.filter(c => c.createdAt <= endDate);
    
    // 최신순 정렬 보장
    return data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [credits, searchTerm, filterType, startDate, endDate]);

  // ★ [추가] 현재 페이지에 보여줄 데이터 슬라이싱
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCredits = filteredCredits.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCredits.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
        setCurrentPage(pageNumber);
    }
  };

  const summary = useMemo(() => {
    // 요약은 전체 데이터를 기준으로 계산 (페이지네이션 영향 X)
    return filteredCredits.reduce((acc, curr) => ({
      totalPrincipal: acc.totalPrincipal + (parseFloat(curr.principal) || 0),
      totalRepaid: acc.totalRepaid + (parseFloat(curr.repaidAmount) || 0),
      totalOutstanding: acc.totalOutstanding + ((parseFloat(curr.principal) || 0) - (parseFloat(curr.repaidAmount) || 0))
    }), { totalPrincipal: 0, totalRepaid: 0, totalOutstanding: 0 });
  }, [filteredCredits]);

  const handleRepayClick = (loan) => {
    setSelectedLoan(loan);
    setRepayAmount('');
    setIsRepayModalOpen(true);
  };

  const handleRepaySubmit = async () => {
    const amount = parseNum(repayAmount);
    if (!amount || amount <= 0) return alert("Please enter amount.");
    
    if (window.confirm(`Repay ${fmt(amount)}? (Deduct from agent balance)`)) {
        const success = await repayLoan(selectedLoan.loanId, amount);
        if (success) {
            alert("Repayment successful.");
            setIsRepayModalOpen(false);
        }
    }
  };

  return (
    <div style={{ padding: '30px', color: '#1e293b' }}>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, display:'flex', alignItems:'center', gap:'10px' }}>
          <DollarSign size={28} color="#7c3aed"/> {t('credit_title')}
        </h2>
        
        <div style={{ padding:'10px 20px', backgroundColor:'#1e293b', borderRadius:'8px', color:'#fbbf24', display:'flex', alignItems:'center', gap:'10px', boxShadow:'0 4px 6px rgba(0,0,0,0.3)' }}>
            <ShieldCheck size={20} />
            <div>
                <div style={{ fontSize:'11px', color:'#94a3b8' }}>SYSTEM VAULT</div>
                <div style={{ fontSize:'20px', fontWeight:'bold' }}>₱ {fmt(creditVault?.balance || 0)}</div>
            </div>
        </div>
      </div>

      {/* 요약 카드 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '25px' }}>
        <div style={{ background: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', borderLeft: '5px solid #3b82f6', display:'flex', flexDirection:'column', alignItems:'flex-end' }}>
          <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '5px', width:'100%', textAlign:'left' }}>{t('total_loan')}</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1e293b' }}>{fmt(summary.totalPrincipal)}</div>
        </div>
        <div style={{ background: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', borderLeft: '5px solid #10b981', display:'flex', flexDirection:'column', alignItems:'flex-end' }}>
          <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '5px', width:'100%', textAlign:'left' }}>{t('total_repaid')}</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#059669' }}>{fmt(summary.totalRepaid)}</div>
        </div>
        <div style={{ background: '#fff1f2', padding: '25px', borderRadius: '12px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', borderLeft: '5px solid #f43f5e', display:'flex', flexDirection:'column', alignItems:'flex-end' }}>
          <div style={{ fontSize: '14px', color: '#9f1239', marginBottom: '5px', fontWeight:'bold', width:'100%', textAlign:'left' }}>{t('total_outstanding')}</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#e11d48' }}>{fmt(summary.totalOutstanding)}</div>
        </div>
      </div>

      {/* 필터 바 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', backgroundColor:'#f8fafc', padding:'15px', borderRadius:'8px', alignItems:'center', flexWrap:'wrap', gap:'10px' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          {['All', 'Active', 'Settled'].map(type => (
            <button key={type} onClick={() => setFilterType(type)} 
              style={{ padding: '8px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontWeight: 'bold', backgroundColor: filterType === type ? '#7c3aed' : '#e2e8f0', color: filterType === type ? 'white' : '#64748b' }}>
              {type === 'All' ? t('tab_all') : type === 'Active' ? t('tab_active_loan') : t('tab_settled_loan')}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems:'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', background: 'white', border: '1px solid #ccc', borderRadius: '6px', padding: '5px 10px' }}>
                <Calendar size={16} color="#64748b" style={{ marginRight: '8px' }} />
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ border: 'none', outline: 'none', fontSize: '13px', color: '#334155', cursor:'pointer' }} />
                <span style={{ margin: '0 8px', color: '#64748b' }}>~</span>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ border: 'none', outline: 'none', fontSize: '13px', color: '#334155', cursor:'pointer' }} />
            </div>
            <button onClick={() => { setStartDate(''); setEndDate(''); setSearchTerm(''); setFilterType('All'); }} title="Reset" style={{ padding: '8px', background: '#e2e8f0', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><RotateCcw size={16} color="#475569" /></button>
            <div style={{ position: 'relative' }}>
                <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder={t('search')} style={{ padding: '8px 8px 8px 35px', borderRadius: '6px', border: '1px solid #ccc', width: '250px' }} />
                <Search size={18} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            </div>
        </div>
      </div>

      <div style={{ overflowX: 'auto', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', textAlign: 'center' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', height:'50px' }}>
              <th style={{ padding:'15px' }}>{t('th_loan_id')}</th>
              <th>{t('date')}</th>
              <th>{t('menu_agents')}</th>
              <th>{t('th_linked_game')}</th>
              <th style={{ textAlign:'right' }}>{t('th_principal')}</th>
              <th style={{ textAlign:'right' }}>{t('th_repaid')}</th>
              <th style={{ textAlign:'right' }}>{t('th_re_balance')}</th>
              <th>{t('status')}</th>
              <th>{t('action')}</th>
            </tr>
          </thead>
          <tbody>
            {/* ★ [수정] filteredCredits -> currentCredits (페이지네이션 적용) */}
            {currentCredits.length > 0 ? currentCredits.map(credit => {
                const agent = agents.find(a => a.code === credit.agentId);
                const balance = (parseFloat(credit.principal) || 0) - (parseFloat(credit.repaidAmount) || 0);
                const isSettled = credit.status === 'Settled' || balance <= 0;

                return (
                    <tr key={credit.id} style={{ borderBottom: '1px solid #f1f5f9', height:'60px' }}>
                        <td style={{ fontWeight:'bold', color:'#64748b' }}>{credit.loanId}</td>
                        <td style={{ color:'#64748b' }}>{new Date(credit.createdAt).toLocaleDateString()}</td>
                        <td style={{ textAlign:'left', paddingLeft:'20px' }}>
                            {agent ? (
                                <div onClick={() => handleAgentClick(agent.code)} style={{ cursor: 'pointer' }}>
                                    <span style={{ fontWeight:'bold', color: '#2563eb', textDecoration:'underline' }}>{agent.code}</span>
                                    <span style={{ fontSize:'12px', color:'#1e293b', marginLeft:'5px' }}>{agent.engSurname} {agent.engGivenName}</span>
                                    {agent.nativeName && <span style={{ fontSize:'12px', color:'#64748b', marginLeft:'4px' }}>({agent.nativeName})</span>}
                                </div>
                            ) : credit.agentId}
                        </td>
                        <td>{credit.gameId ? <span style={{ padding:'4px 8px', background:'#f1f5f9', borderRadius:'4px', fontSize:'12px', fontWeight:'bold', color:'#475569' }}>{credit.gameId}</span> : '-'}</td>
                        <td style={{ textAlign:'right', fontWeight:'bold' }}>{fmt(credit.principal)}</td>
                        <td style={{ textAlign:'right', color:'#059669' }}>{fmt(credit.repaidAmount)}</td>
                        <td style={{ textAlign:'right', fontWeight:'bold', color: balance > 0 ? '#dc2626' : '#94a3b8' }}>{fmt(balance)}</td>
                        <td><span style={{ padding:'5px 10px', borderRadius:'15px', fontSize:'12px', fontWeight:'bold', backgroundColor: isSettled ? '#dcfce7' : '#fee2e2', color: isSettled ? '#166534' : '#991b1b' }}>{isSettled ? t('settled') : t('active')}</span></td>
                        <td>{!isSettled && <button onClick={() => handleRepayClick(credit)} style={{ padding:'6px 12px', background:'#7c3aed', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'bold', display:'flex', alignItems:'center', gap:'4px', margin:'0 auto' }}><ArrowDownLeft size={14}/> Repay</button>}</td>
                    </tr>
                );
            }) : (
                <tr><td colSpan="9" style={{ padding:'40px', color:'#94a3b8' }}>No Data</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ★ [추가] 페이지네이션 컨트롤 */}
      {totalPages > 1 && (
        <div className="no-print" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
            <button 
                onClick={() => handlePageChange(currentPage - 1)} 
                disabled={currentPage === 1}
                style={{ padding: '8px', borderRadius: '50%', border: '1px solid #cbd5e1', background: 'white', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
            >
                <ChevronLeft size={20} />
            </button>
            <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#334155' }}>
                Page {currentPage} of {totalPages}
            </span>
            <button 
                onClick={() => handlePageChange(currentPage + 1)} 
                disabled={currentPage === totalPages}
                style={{ padding: '8px', borderRadius: '50%', border: '1px solid #cbd5e1', background: 'white', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}
            >
                <ChevronRight size={20} />
            </button>
        </div>
      )}

      {isRepayModalOpen && selectedLoan && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1200 }}>
              <div style={{ backgroundColor: 'white', width: '400px', padding: '30px', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'20px' }}>
                      <h3 style={{ margin:0, color:'#1e293b' }}>Manual Repay</h3>
                      <button onClick={() => setIsRepayModalOpen(false)} style={{ background:'none', border:'none', cursor:'pointer' }}><X size={24}/></button>
                  </div>
                  <div style={{ background:'#f8fafc', padding:'15px', borderRadius:'8px', marginBottom:'20px' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'5px', fontSize:'13px', color:'#64748b' }}><span>{t('th_loan_id')}</span><span>{selectedLoan.loanId}</span></div>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'5px', fontSize:'13px', color:'#64748b' }}><span>{t('menu_agents')}</span><span>{selectedLoan.agentId}</span></div>
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:'14px', fontWeight:'bold', color:'#dc2626', marginTop:'10px', paddingTop:'10px', borderTop:'1px dashed #cbd5e1' }}><span>Current Balance</span><span>{fmt((parseFloat(selectedLoan.principal) || 0) - (parseFloat(selectedLoan.repaidAmount) || 0))}</span></div>
                  </div>
                  <div style={{ marginBottom:'20px' }}><label style={{ display:'block', marginBottom:'5px', fontSize:'13px', fontWeight:'bold' }}>{t('amount')}</label><input value={repayAmount} onChange={(e) => setRepayAmount(e.target.value.replace(/[^0-9]/g, '') ? Number(e.target.value.replace(/[^0-9]/g, '')).toLocaleString() : '')} placeholder="0" style={{ width:'100%', padding:'12px', borderRadius:'6px', border:'1px solid #cbd5e1', fontSize:'16px', fontWeight:'bold', color:'#059669' }} /></div>
                  <button onClick={handleRepaySubmit} style={{ width:'100%', padding:'15px', background:'#059669', color:'white', border:'none', borderRadius:'8px', fontWeight:'bold', cursor:'pointer', fontSize:'16px' }}>{t('confirm')}</button>
              </div>
          </div>
      )}
    </div>
  );
};

export default CreditManagement;